<template>
  <!-- The app's Welcome art (welcome_screen.dart WelcomeArt): the Mandelbrot
       shader at .35 behind the rings, drifting on a 10 s back-and-forth,
       then a fade from clear at 45% of the window to the page colour at
       85%. Still under reduced motion (and in the screenshot harness, which
       asks for it); absent where WebGL isn't. -->
  <div class="wf" aria-hidden="true">
    <canvas ref="canvas" class="wf-canvas" />
    <!-- The rings, between the fractal and the fade. -->
    <slot />
    <div class="wf-fade" />
  </div>
</template>

<script>
// shaders/mandelbrot.frag on flowstate master, in GLSL ES 1.0. The uniforms
// keep the app's names in comments; the painter's arguments are the ones
// WelcomeArt passes (center -0.5,0; 6 folds; rotation .1; radius .1).
const VERT = `
attribute vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform vec2 u_res;        // width, height
uniform float u_zoom;      // zoom
uniform vec2 u_center;     // center
uniform float u_time;      // time
uniform float u_folds;     // numFolds
uniform float u_rot;       // rotationSpeed
uniform float u_radius;    // radiusEffect
uniform float u_connected; // isConnected

vec3 palette(float t) {
  float colorPhase = sin(u_time * 0.2) * 0.5;
  float colorPhase2 = cos(u_time * 0.3) * 0.5;
  vec3 connectedColor = vec3(0.7 + colorPhase, 0.0, 0.8 + colorPhase2);
  vec3 disconnectedColor = vec3(0.8 + colorPhase, 0.2, 0.2 + colorPhase2);
  vec3 b = mix(disconnectedColor, connectedColor, u_connected);
  vec3 c = vec3(0.5 + colorPhase2, 0.0, 1.0 + colorPhase);
  vec3 d = vec3(colorPhase, 0.2 + colorPhase2, 1.0);
  vec3 e = vec3(0.0, 0.5 + colorPhase, 1.0 + colorPhase2);
  float t1 = fract(t * 4.0);
  vec3 color;
  if (t < 0.25) color = mix(vec3(0.0), b, t1);
  else if (t < 0.5) color = mix(b, c, t1);
  else if (t < 0.75) color = mix(c, d, t1);
  else color = mix(d, e, t1);
  float pulse = 1.0 + sin(u_time * 0.15) * 0.3;
  return color * pulse;
}

float mandelbrot(vec2 c) {
  vec2 z = vec2(0.0);
  float iter = 0.0;
  float zoomFactor = exp(log(100.0) * u_rot);
  float modulation = sin(u_time * zoomFactor) * 0.2;
  for (int i = 0; i < 100; i++) {
    z = vec2(
      z.x * z.x - z.y * z.y + modulation * cos(u_time * zoomFactor),
      2.0 * z.x * z.y + modulation * sin(u_time * zoomFactor)
    ) + c;
    if (dot(z, z) > 4.0) {
      iter = float(i);
      break;
    }
  }
  return iter;
}

void main() {
  // Flutter's FlutterFragCoord() counts y down from the top.
  vec2 fragCoord = vec2(gl_FragCoord.x, u_res.y - gl_FragCoord.y);
  vec2 uv = (fragCoord * 2.0 - u_res) / min(u_res.x, u_res.y);
  vec2 polar = vec2(length(uv), atan(uv.y, uv.x));
  float numFolds = max(2.0, u_folds);
  float angleOffset = sin(u_time * u_rot) * 0.5;
  float foldAngle = (2.0 * 3.14159) / numFolds;
  polar.y = mod(polar.y + angleOffset, foldAngle) - foldAngle * 0.5;
  polar.x *= 1.0 + sin(u_time * 0.2) * u_radius;
  vec2 kaleidoUV = vec2(polar.x * cos(polar.y), polar.x * sin(polar.y));
  float zoomFactor = exp(log(100.0) * u_rot);
  float breatheX = sin(u_time * zoomFactor) * 0.03;
  float breatheY = cos(u_time * (1.2 * zoomFactor)) * 0.03;
  vec2 c = kaleidoUV / u_zoom + vec2(u_center.x + breatheX, u_center.y + breatheY);
  float t = mandelbrot(c) / 100.0;
  float connectionPulse = 1.0 + sin(u_time * (1.0 + u_connected * 0.5)) * 0.3;
  gl_FragColor = vec4(palette(t) * connectionPulse, 1.0);
}
`;

// WelcomeArt's AnimationController: 10 s, repeat(reverse: true), linear.
const PERIOD_MS = 10000;
// The frame shown when the art holds still.
const STILL = 0.3;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

export default {
  name: "WelcomeFractal",
  mounted() {
    this.start();
  },
  beforeUnmount() {
    this.stop();
  },
  methods: {
    start() {
      const canvas = this.$refs.canvas;
      let gl = null;
      try {
        gl =
          canvas.getContext("webgl", { antialias: false, alpha: false, preserveDrawingBuffer: true }) ||
          null;
      } catch (e) {
        gl = null;
      }
      if (!gl) return;
      const vs = compile(gl, gl.VERTEX_SHADER, VERT);
      const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
      if (!vs || !fs) return;
      const prog = gl.createProgram();
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
      gl.useProgram(prog);
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, "p");
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      const u = (n) => gl.getUniformLocation(prog, n);
      this.gl = gl;
      this.uniforms = {
        res: u("u_res"),
        zoom: u("u_zoom"),
        center: u("u_center"),
        time: u("u_time"),
        folds: u("u_folds"),
        rot: u("u_rot"),
        radius: u("u_radius"),
        connected: u("u_connected"),
      };
      gl.uniform2f(this.uniforms.center, -0.5, 0);
      gl.uniform1f(this.uniforms.folds, 6);
      gl.uniform1f(this.uniforms.rot, 0.1);
      gl.uniform1f(this.uniforms.radius, 0.1);
      // The web has no signed-out socket to follow: the connected look.
      gl.uniform1f(this.uniforms.connected, 1);

      this.motion = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
      this.onMotion = () => this.schedule();
      this.motion?.addEventListener?.("change", this.onMotion);
      this.onResize = () => this.draw(this.value ?? STILL);
      window.addEventListener("resize", this.onResize);
      this.t0 = performance.now();
      this.schedule();
    },
    stop() {
      cancelAnimationFrame(this.raf);
      this.raf = null;
      this.motion?.removeEventListener?.("change", this.onMotion);
      if (this.onResize) window.removeEventListener("resize", this.onResize);
      const ext = this.gl && this.gl.getExtension("WEBGL_lose_context");
      if (ext) ext.loseContext();
      this.gl = null;
    },
    still() {
      return !!(this.motion && this.motion.matches);
    },
    schedule() {
      cancelAnimationFrame(this.raf);
      this.raf = null;
      if (!this.gl) return;
      if (this.still()) {
        this.draw(STILL);
        return;
      }
      const tick = (now) => {
        const phase = ((now - this.t0) % (2 * PERIOD_MS)) / PERIOD_MS;
        this.draw(phase <= 1 ? phase : 2 - phase);
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    },
    draw(v) {
      const gl = this.gl;
      const canvas = this.$refs.canvas;
      if (!gl || !canvas) return;
      this.value = v;
      // One device pixel per CSS pixel at most: the art sits at .35 behind
      // a scrim, so a denser buffer only costs fill rate.
      const w = Math.max(1, Math.round(canvas.clientWidth));
      const h = Math.max(1, Math.round(canvas.clientHeight));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
      gl.uniform2f(this.uniforms.res, w, h);
      gl.uniform1f(this.uniforms.zoom, 0.5 + 1.5 * v);
      gl.uniform1f(this.uniforms.time, v * 2 * Math.PI);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    },
  },
};
</script>

<style scoped>
/* Behind everything in the window, then the fade to the page colour. */
.wf {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}
.wf-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.35;
}
.wf-fade {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 45%, var(--cl-bg) 85%);
}
</style>
